import { Component, EventEmitter, Input, OnChanges, OnInit, AfterViewInit, Output, SimpleChanges, ViewChild, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { DataService, YearwiseResponse, Exam, Paper, Year } from '../../../services/data.service';
import { RoleService } from '../../../services/role.service';
import { TreeEditService } from '../../../services/tree-edit.service';
import { TreeService } from '../../../services/tree.service';
import { Subject, takeUntil, take } from 'rxjs';
// Import dialog components
import { TopicDialogComponent } from '../../dialogs/topic-dialog/topic-dialog.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';

// Import the same node types as the navigation-app component
interface SubjectNode {
  name: string;
  children?: SubjectNode[];
  nameHindi?: string;
  id?: number;
  subjectId?: number;
  level?: number;
  type: 'subject' | 'section' | 'topic' | 'subtopic';
  isHighlighted?: boolean;
  topicId?: string;
  isEditing?: boolean;
}

interface YearwiseNode {
  name: string;
  children?: YearwiseNode[];
  nameHindi?: string;
  id?: number;
  year_id?: number;
  level?: number;
  type: 'exam' | 'examType' | 'year';
  isHighlighted?: boolean;
}

interface TopicNode {
  name: string;
  children?: TopicNode[];
  id?: number;
  nameHindi?: string;
  level?: number;
  type: 'topic' | 'subtopic';
}

@Component({
  selector: 'app-single-tree',
  standalone: true,
  imports: [
    NgIf, NgForOf,
    MatToolbarModule, MatButtonModule, MatIconModule, MatMenuModule,
    MatTooltipModule, ScrollingModule, MatProgressSpinnerModule, MatTreeModule,
    FormsModule, MatSlideToggleModule, MatDialogModule
  ],
  templateUrl: './single-tree.component.html',
  styleUrls: ['./single-tree.component.css']
})
export class SingleTreeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Inputs from parent component
  @Input() p1Mode: string = 'topicwise'; // Default mode
  @Input() p1Subjects: any[] = [];
  @Input() p1SubMap: any = {};
  @Input() p1YearwiseExams: any[] = [];
  @Input() p1YearwiseLower: any = {};
  @Input() hindiNameMap: {[key: string]: string} = {};
  @Input() isLoadingSubjects: boolean = false;
  @Input() isLoadingSections: boolean = false;
  @Input() searchTerm: string = ''; // Add search term input
  @Input() filterMode: boolean = false; // Add filter mode flag

  // Additional inputs for topics and subtopics
  @Input() p2TopicsMap: any = {};
  @Input() p2SubTopicsMap: any = {};
  @Input() isLoadingTopics: boolean = false;
  @Input() isLoadingSubtopics: boolean = false;
  @Input() topicIdMap: any = {};
  @Input() p2Years: {[key: string]: string[]} = {};

  // Outputs to parent component
  @Output() modeChange = new EventEmitter<string>();
  @Output() subjectClick = new EventEmitter<any>();
  @Output() subItemClick = new EventEmitter<any>();
  @Output() topicClick = new EventEmitter<any>();
  @Output() subTopicClick = new EventEmitter<any>();
  @Output() yearwiseExamClick = new EventEmitter<any>();
  @Output() yearwiseLowerClick = new EventEmitter<any>();
  @Output() yearClick = new EventEmitter<any>();
  @Output() hindiNamesRequested = new EventEmitter<void>();
  @Output() topicsLoaded = new EventEmitter<any>();
  @Output() subtopicsLoaded = new EventEmitter<any>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() searchChange = new EventEmitter<{term: string, filterMode: boolean}>();
  @Output() yearsListGenerated = new EventEmitter<string[]>();
  @Output() hierarchyPathChange = new EventEmitter<string[]>();
  @Output() refreshTreeRequested = new EventEmitter<void>();
  @Output() editModeChange = new EventEmitter<boolean>();

  // Tree controls and data sources
  treeControl = new NestedTreeControl<SubjectNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<SubjectNode>();
  treeData: SubjectNode[] = [];
  isTreeExpanded = true; // Start with expanded tree

  yearwiseTreeControl = new NestedTreeControl<YearwiseNode>(node => node.children);
  yearwiseDataSource = new MatTreeNestedDataSource<YearwiseNode>();
  yearwiseTreeData: YearwiseNode[] = [];
  isYearwiseTreeExpanded = true; // Start with expanded tree

  // Track selected nodes
  selectedNode: SubjectNode | null = null;
  selectedYearwiseNode: YearwiseNode | null = null;

  // Track current selections
  selectedSubject: string | null = null;
  selectedSubItem: string | null = null;
  selectedTopic: string | null = null;
  selectedSubTopic: string | null = null;
  selectedYearwiseExam: string | null = null;
  selectedYearwiseLower: string | null = null;
  selectedYear: string | null = null;

  // Language settings
  selectedLanguage: string = 'bilingual'; // Default
  languageOptions = [
    { value: 'bilingual', label: 'Bilingual' },
    { value: 'englishonly', label: 'English Only' },
    { value: 'hindionly', label: 'Hindi Only' }
  ];

  // Search results tracking
  searchResults: string[] = [];

  // Search functionality properties
  isSearchExpanded = false;
  @ViewChild('searchInput') searchInput!: ElementRef;

  // Add a reference to the language menu trigger
  @ViewChild(MatMenuTrigger) languageMenuTrigger!: MatMenuTrigger;

  // Array to store the path from root to selected node
  selectedNodePath: SubjectNode[] = [];
  selectedYearwiseNodePath: YearwiseNode[] = [];

  // Added for search debounce
  private searchTimeout: any;

  // Edit tree mode
  isEditMode: boolean = false;
  isInternalUser: boolean = false;
  currentLayout: string = 'default';
  editingNode: any = null;

  @Input() currentLayoutView: string = 'default';

  // Add destroy Subject for takeUntil operator
  private destroy$ = new Subject<void>();

  // Track if refresh is in progress to prevent multiple calls
  isRefreshInProgress = false;

  // Prevent multiple calls to expandAllNodes
  private isExpandingNodes = false;

  constructor(
    private dataService: DataService,
    private changeDetectorRef: ChangeDetectorRef,
    private roleService: RoleService,
    private treeEditService: TreeEditService,
    private dialog: MatDialog,
    private treeService: TreeService // Add TreeService
  ) {}

  ngOnInit(): void {
    // Initialize selection paths
    this.selectedNodePath = [];
    this.selectedYearwiseNodePath = [];

    // Request Hindi names from parent component
    this.hindiNamesRequested.emit();

    // Check if user is internal
    this.checkUserRole();

    // Subscribe to role changes
    this.roleService.currentRole$.subscribe(role => {
      this.isInternalUser = role === 'internal';
      console.log('SingleTree - Role updated:', role, 'isInternalUser:', this.isInternalUser);
      this.changeDetectorRef.detectChanges();
    });

    // Subscribe to tree data changes
    this.treeService.getTreeData().pipe(
      takeUntil(this.destroy$)
    ).subscribe(treeData => {
      console.log('Tree data updated in SingleTreeComponent');
      this.updateTreeData();
    });

    // Subscribe to loading state
    this.treeService.getLoadingState().pipe(
      takeUntil(this.destroy$)
    ).subscribe(loading => {
      // Handle loading state if needed
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('SingleTree - OnChanges');

    // If the layout view changes to 1-column, fetch data from API only if needed
    if (changes['currentLayoutView']) {
      const newLayout = changes['currentLayoutView'].currentValue;
      const oldLayout = changes['currentLayoutView'].previousValue;
      
      console.log(`SingleTree - Layout changed from ${oldLayout} to ${newLayout}`);
      
      // Only fetch from API if:
      // 1. We're changing TO 1-column layout AND
      // 2. We haven't loaded data yet OR we're coming from a different layout
      if (newLayout === '1-column' && (oldLayout !== '1-column' || !this.treeData || this.treeData.length === 0)) {
        console.log('SingleTree - Need to load data from API for 1-column layout');
        
        // Call API only if we don't already have data or refresh is not in progress
        if (!this.isRefreshInProgress && this.currentLayoutView === '1-column') {
          console.log('SingleTree - Triggering API refresh for 1-column layout');
          this.refreshTopicTree();
        }
      } else {
        console.log('SingleTree - No need to reload data for layout change');
      }
    }
    
    // Process search term changes
    if (changes['searchTerm'] || changes['filterMode']) {
      if (this.searchTerm) {
        this.performSearch();
      } else {
        this.clearSearch();
      }
    }

    // Update tree data when it changes
    if (changes['p1Subjects'] || changes['p1SubMap'] || changes['p2TopicsMap'] || changes['p2SubTopicsMap'] || changes['hindiNameMap'] || changes['topicIdMap']) {
      console.log('SingleTree - Tree data updated in SingleTreeComponent');
      
      // Only update if not in 1-column view (which gets data directly from API)
      if (this.currentLayoutView !== '1-column') {
        this.updateTreeData();
      }
    }
  }

  ngAfterViewInit(): void {
    // After view init, ensure trees are expanded by default
    setTimeout(() => {
      if (this.currentLayoutView === '1-column') {
        if (this.p1Mode === 'topicwise') {
          this.refreshTopicTree();
        } else if (this.p1Mode === 'yearwise') {
          this.fetchYearwiseData();
        }
      }
    }, 500);
  }

  // Update the topicwise tree data
  updateTreeData(): void {
    console.log('SingleTree - Updating tree data');
    
    // In case of 1-column view, only update from TreeService, don't use pre-loaded data
    if (this.currentLayoutView === '1-column') {
      this.treeService.getTreeData().pipe(take(1)).subscribe(treeData => {
        if (treeData) {
          console.log('SingleTree - Using data from TreeService');
          
          // Use the tree data from TreeService directly
          if (this.p1Mode === 'topicwise') {
            const subjectNodes: SubjectNode[] = [];
            
            // Convert the tree data to SubjectNode format
            Object.keys(treeData.subjects).forEach(subjectName => {
              const subject = treeData.subjects[subjectName];
              const subjectNode: SubjectNode = {
                name: subjectName,
                children: [],
                nameHindi: subject.hindiName || this.hindiNameMap[subjectName] || '',
                type: 'subject'
              };
              
              // Process sections for this subject
              if (subject.sections) {
                this.buildTopicwiseTreeFromData(subject, subjectNode);
              }
              
              subjectNodes.push(subjectNode);
            });
            
            // Update dataSource
            this.treeData = subjectNodes;
            this.dataSource.data = this.treeData;
            this.initializeTreeNodes(this.treeData);
            
            // Expand nodes if tree was expanded previously
            if (this.isTreeExpanded) {
              this.expandAllNodes();
            }
          } else if (this.p1Mode === 'yearwise') {
            // Update yearwise data in a similar way
            this.fetchYearwiseData();
          }
        }
      });
    } else {
      // Legacy behavior for non-1-column view
      if (this.p1Mode === 'topicwise') {
        // Build topicwise tree
        const subjectNodes: SubjectNode[] = this.p1Subjects.map(subjectName => {
          const topicInfo = this.topicIdMap && this.topicIdMap[subjectName];
          return {
            name: subjectName,
            children: this.buildTopicwiseTree(subjectName),
            nameHindi: (topicInfo && topicInfo.hindiName) || this.hindiNameMap[subjectName] || '',
            type: 'subject'
          };
        });

        // Update dataSource
        this.treeData = subjectNodes;
        this.dataSource.data = this.treeData;
        this.initializeTreeNodes(this.treeData);
        
        // Expand nodes if tree was expanded previously
        if (this.isTreeExpanded) {
          this.expandAllNodes();
        }
      } else if (this.p1Mode === 'yearwise') {
        this.updateYearwiseTreeData();
      }
    }
  }

  private selectFirstCompletePath(): void {

    // Find first subject node
    if (this.treeData && this.treeData.length > 0) {
      const firstSubject = this.treeData[0];
      if (firstSubject) {
        // Select the subject
        this.onNodeSelect(firstSubject);

        // Find and select first section if available
        if (firstSubject.children && firstSubject.children.length > 0) {
          const firstSection = firstSubject.children[0];
          if (firstSection) {
            this.onNodeSelect(firstSection);

            // Find and select first topic if available
            if (firstSection.children && firstSection.children.length > 0) {
              const firstTopic = firstSection.children[0];
              if (firstTopic) {
                this.onNodeSelect(firstTopic);

                // Find and select first subtopic if available
                if (firstTopic.children && firstTopic.children.length > 0) {
                  const firstSubtopic = firstTopic.children[0];
                  if (firstSubtopic) {
                    this.onNodeSelect(firstSubtopic);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  // Update the yearwise tree data
  fetchYearwiseData(): void {
    // Reset all selections to force auto-selection
    this.selectedYearwiseExam = null;
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Set loading state
    this.isLoadingSubjects = true;

    this.dataService.getYearwiseData()
      .subscribe(
        (response: YearwiseResponse) => {
          // Update loading state
          this.isLoadingSubjects = false;
          
          if (response && response.exams) {
            // Build yearwise tree structure directly from API response
            this.yearwiseTreeData = response.exams.map(exam => {
              const examNode: YearwiseNode = {
                name: exam.exam_name,
                type: 'exam',
                children: []
              };
              
              // Add papers as children
              if (exam.papers && exam.papers.length > 0) {
                examNode.children = exam.papers.map(paper => {
                  const paperNode: YearwiseNode = {
                    name: paper.paper_name,
                    type: 'examType',
                    children: []
                  };
                  
                  // Add years as children of papers
                  if (paper.years && paper.years.length > 0) {
                    paperNode.children = paper.years
                      .sort((a, b) => b.year - a.year) // Sort years in descending order
                      .map(year => {
                        return {
                          name: year.year.toString(),
                          type: 'year' as const
                        };
                      });
                  }
                  
                  return paperNode;
                });
              }
              
              return examNode;
            });
            
            // Update the yearwise data source
            this.yearwiseDataSource.data = this.yearwiseTreeData;
            
            // Expand all yearwise nodes
            this.isYearwiseTreeExpanded = true;
            setTimeout(() => {
              this.expandAllYearwiseNodes();
              this.autoSelectFirstItems();
            }, 300);
          }
        },
        (error: any) => {
          this.isLoadingSubjects = false;
          console.error('SingleTree - Error fetching yearwise data:', error);
        }
      );
  }

  // Update the yearwise tree data (This method is now only used internally)
  updateYearwiseTreeData(): void {
    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      console.warn('SingleTree - No yearwise data available, fetching from API');
      this.fetchYearwiseData();
      return;
    }

    // Update the data source with the existing yearwise tree data
    this.yearwiseDataSource.data = this.yearwiseTreeData;

    // Always expand all nodes immediately after data is loaded
    this.isYearwiseTreeExpanded = true;
    setTimeout(() => {
      this.expandAllYearwiseNodes();
      this.autoSelectFirstItems();
    }, 300);
  }

  // Helper function to initialize tree nodes and ensure all levels are properly structured
  private initializeTreeNodes(nodes: SubjectNode[]): void {
    if (!nodes) return;

    nodes.forEach(node => {
      // Ensure children array exists
      if (!node.children) {
        node.children = [];
      }

      // Assign level based on node type if not already assigned
      if (node.level === undefined) {
        if (node.type === 'subject') {
          node.level = 0;
        } else if (node.type === 'section') {
          node.level = 1;
        } else if (node.type === 'topic') {
          node.level = 2;
        } else if (node.type === 'subtopic') {
          node.level = 3;
        }
      }

      // Recursively initialize child nodes
      if (node.children && node.children.length > 0) {
        this.initializeTreeNodes(node.children);
      }
    });
  }

  onModeChange(mode: string): void {
    console.log('SingleTree - Mode changed to:', mode);

    // Reset selection paths when changing modes
    this.selectedNodePath = [];
    this.selectedYearwiseNodePath = [];

    // Update mode
    this.p1Mode = mode;
    this.modeChange.emit(mode);

    // If in 1-column layout, fetch data from API based on mode
    if (this.currentLayoutView === '1-column') {
      if (mode === 'yearwise') {
        this.fetchYearwiseData();
      } else if (mode === 'topicwise') {
        this.refreshTopicTree();
      }
    }
  }

  /**
   * Recursively finds nodes that match the search term
   * @param nodes Node array to search through
   * @param searchTerm Term to search for
   * @param results Array to collect matching nodes
   * @returns true if any nodes matched
   */
  findMatchingNodes<T extends SubjectNode | YearwiseNode>(nodes: T[], searchTerm: string, results: any[]): boolean {
    if (!nodes || !searchTerm || searchTerm.length < 3 || nodes.length === 0) {
      return false;
    }

    const searchTermLower = searchTerm.toLowerCase();
    let hasMatch = false;

    for (const node of nodes) {
      // Reset highlight state for this node
      node.isHighlighted = false;

      // Check if this node's name matches
      const nodeName = node.name;
      const nodeNameLower = nodeName.toLowerCase();
      // Also check Hindi name if available through hindiNameMap
      const hindiName = this.hindiNameMap && this.hindiNameMap[nodeName] ? this.hindiNameMap[nodeName].toLowerCase() : '';
      let nodeMatches = nodeNameLower.includes(searchTermLower) || hindiName.includes(searchTermLower);

      // Check if any of the children match
      let childrenMatch = false;
      if (node.children && node.children.length > 0) {
        childrenMatch = this.findMatchingNodes<T>(node.children as T[], searchTerm, results);
      }

      // Mark this node as highlighted if it matches or any of its children match
      // Note: The actual text highlighting is now handled by the formatNodeName method
      // The isHighlighted property is still needed for filter mode and to expand parent nodes
      if (nodeMatches) {
        node.isHighlighted = true;
        results.push(node);
        console.log(`Match found for "${searchTerm}" in node:`, nodeName);
        hasMatch = true;
      } else if (childrenMatch) {
        // If any children match, this node should be highlighted for the filter view
        node.isHighlighted = true;
        hasMatch = true;
        console.log(`Node "${nodeName}" highlighted because children match`);
      }
    }

    return hasMatch;
  }

  // Perform search and highlight matching nodes
  performSearch(): void {
    console.log("SingleTree: performSearch called with searchTerm:", this.searchTerm, "filterMode:", this.filterMode);

    if (!this.treeData || this.treeData.length === 0) {
      console.warn("SingleTree: performSearch - Tree data is empty, postponing search");
      // Save the term for later when the data is loaded
      return;
    }

    const searchTerm = this.searchTerm ? this.searchTerm.trim() : '';
    this.searchResults = [];

    // Reset all highlights first
    console.log("SingleTree: Resetting highlights");
    this.resetHighlights(this.treeData as (SubjectNode | YearwiseNode)[]);
    if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
      this.resetHighlights(this.yearwiseTreeData as (SubjectNode | YearwiseNode)[]);
    }

    // If search term is cleared or too short, just refresh the data sources
    if (!searchTerm || searchTerm.length < 3) {
      console.log("SingleTree: Empty or too short search term, resetting trees");
      this.dataSource.data = [...this.treeData];
      if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
        this.yearwiseDataSource.data = [...this.yearwiseTreeData];
      }
      return;
    }

    // Find matching nodes in topicwise tree
    console.log("SingleTree: Searching topicwise tree for:", searchTerm);
    const topicwiseMatches = this.findMatchingNodes<SubjectNode>(this.treeData, searchTerm, this.searchResults);

    // Find matching nodes in yearwise tree
    if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
      console.log("SingleTree: Searching yearwise tree for:", searchTerm);
      const yearwiseMatches = this.findMatchingNodes<YearwiseNode>(this.yearwiseTreeData, searchTerm, this.searchResults);
    }

    // Expand matched nodes for visibility
    if (this.searchResults && this.searchResults.length > 0) {
      console.log(`SingleTree: Expanding ${this.searchResults.length} matched nodes`);
      this.expandMatchedNodes<SubjectNode>(this.treeData, this.treeControl);
      if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
        this.expandMatchedNodes<YearwiseNode>(this.yearwiseTreeData, this.yearwiseTreeControl);
      }
    } else {
      console.log("SingleTree: No matches found for:", searchTerm);
    }

    // If filter mode is enabled, filter the tree data to only show matching nodes
    if (this.filterMode) {
      console.log("SingleTree: Filter mode enabled, filtering trees");
      // For topicwise tree, clone and filter
      const filteredTopicwiseData = this.filterTreeData(this.treeData, true);
      console.log("SingleTree: Filtered topicwise data:", filteredTopicwiseData);
      this.dataSource.data = filteredTopicwiseData;

      // For yearwise tree, clone and filter
      if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
        const filteredYearwiseData = this.filterTreeData(this.yearwiseTreeData, true);
        console.log("SingleTree: Filtered yearwise data:", filteredYearwiseData);
        this.yearwiseDataSource.data = filteredYearwiseData;
      }

      // Auto-expand all nodes in filtered trees to make results visible
      setTimeout(() => {
        console.log("SingleTree: Auto-expanding all filtered nodes");
        if (this.p1Mode === 'topicwise') {
          this.expandAllFilteredNodes(this.dataSource.data as SubjectNode[], this.treeControl);
        } else {
          this.expandAllFilteredNodes(this.yearwiseDataSource.data as YearwiseNode[], this.yearwiseTreeControl);
        }
      }, 0);
    } else {
      // Update data sources to refresh views with all nodes
      console.log("SingleTree: Filter mode disabled, showing all nodes with highlights");
      this.dataSource.data = [...this.treeData];
      if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
        this.yearwiseDataSource.data = [...this.yearwiseTreeData];
      }
    }

    console.log(`SingleTree: Found ${this.searchResults.length} matches for "${searchTerm}":`, this.searchResults);

    // Force change detection after a small delay to ensure highlighting is reflected
    setTimeout(() => {
      // Create a shallow copy to force change detection
      this.dataSource.data = [...this.dataSource.data];
      if (this.yearwiseTreeData && this.yearwiseTreeData.length > 0) {
        this.yearwiseDataSource.data = [...this.yearwiseDataSource.data];
      }
      console.log("SingleTree: Trees updated to show highlighting and filtering");
    }, 100);
  }

  // Reset highlights for all nodes
  resetHighlights<T extends SubjectNode | YearwiseNode>(nodes: T[]): void {
    if (!nodes || nodes.length === 0) return;

    for (const node of nodes) {
      node.isHighlighted = false;

      if (node.children && node.children.length > 0) {
        this.resetHighlights(node.children as T[]);
      }
    }
  }

  // Expand nodes that have matches
  expandMatchedNodes<T extends SubjectNode | YearwiseNode>(
      nodes: T[],
      treeControl: NestedTreeControl<T>
  ): void {
    if (!nodes || nodes.length === 0) return;

    for (const node of nodes) {
      // If this node or any of its children are highlighted, expand it
      if (node.isHighlighted && node.children && node.children.length > 0) {
        treeControl.expand(node);

        // Also expand its children that might be highlighted
        this.expandMatchedNodes(node.children as T[], treeControl);
      }
    }
  }

  // Format node name based on selected language and available Hindi names
  formatNodeName(englishName: string, hindiName?: string): string {
    try {
      if (!englishName) return '';

      // Helper function to escape HTML special characters
      const escapeHtml = (text: string): string => {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
      };

      // Helper function to highlight search terms in a text
      const highlightSearchTerms = (text: string): string => {
        if (!text || !this.searchTerm || !this.searchTerm.trim() || this.searchTerm.trim().length < 3) {
          return escapeHtml(text || '');
        }

        const searchTermLower = this.searchTerm.trim().toLowerCase();
        const textLower = text.toLowerCase();

        if (!textLower.includes(searchTermLower)) {
          return escapeHtml(text);
        }

        let result = '';
        let lastIndex = 0;
        let startIndex = textLower.indexOf(searchTermLower);

        while (startIndex !== -1) {
          // Add the text before the match
          result += escapeHtml(text.substring(lastIndex, startIndex));

          // Add the highlighted search term
          const matchedText = escapeHtml(text.substring(startIndex, startIndex + this.searchTerm.trim().length));
          result += `<span class="search-term-highlight">${matchedText}</span>`;

          // Move past this match
          lastIndex = startIndex + this.searchTerm.trim().length;

          // Look for next occurrence
          startIndex = textLower.indexOf(searchTermLower, lastIndex);
        }

        // Add any remaining text
        result += escapeHtml(text.substring(lastIndex));
        return result;
      };

      // Ensure we have a Hindi name to display
      const actualHindiName = hindiName || this.hindiNameMap[englishName] || '';

      // Normalize language to lowercase for consistent comparison
      const language = this.selectedLanguage.toLowerCase();

      // Format with highlighting based on language settings
      switch (language) {
        case 'bilingual':
          // Add proper classes and structure for bilingual mode
          const highlightedName = highlightSearchTerms(englishName);
          const highlightedHindiName = highlightSearchTerms(actualHindiName || englishName);
          return `<div class="bilingual-container"><div class="english-part">${highlightedName}</div><div class="hindi-part">${highlightedHindiName}</div></div>`;
        case 'englishonly':
          // Only show English name, never the Hindi name
          return `<div class="english-only">${highlightSearchTerms(englishName)}</div>`;
        case 'hindionly':
          // Use Hindi name if available, fallback to English
          return `<div class="hindi-only">${highlightSearchTerms(actualHindiName || englishName)}</div>`;
        default:
          return `<div class="english-only">${highlightSearchTerms(englishName)}</div>`;
      }
    } catch (error) {
      console.error('Error formatting node name:', error);
      return englishName; // Fallback to English name in case of errors
    }
  }

  onLanguageChange(language: string, event: Event): void {
    console.log('SingleTree - Language changed to:', language);
    this.selectedLanguage = language;

    // If switching to Hindi mode but we have few or no Hindi names, request them
    if (this.selectedLanguage === 'hindionly' && Object.keys(this.hindiNameMap).length < 10) {
      this.hindiNamesRequested.emit();
    }

    // Close the menu programmatically
    if (this.languageMenuTrigger) {
      this.languageMenuTrigger.closeMenu();
    }

    // Apply language classes to container based on language selection
    const treeContainer = document.querySelector('.single-tree-container');
    if (treeContainer) {
      // First remove all language classes
      treeContainer.classList.remove('bilingual-mode', 'hindi-only-mode', 'english-only-mode');

      // Add appropriate class based on selection
      if (this.selectedLanguage === 'bilingual') {
        treeContainer.classList.add('bilingual-mode');
      } else if (this.selectedLanguage === 'hindionly') {
        treeContainer.classList.add('hindi-only-mode');
      } else {
        treeContainer.classList.add('english-only-mode');
      }
    }

    // Force refresh of tree displays to reflect language change
    this.updateTreeData();
    this.updateYearwiseTreeData();

    // Maintain expanded state by expanding previously expanded nodes
    setTimeout(() => {
      // For topicwise tree
      if (this.isTreeExpanded) {
        this.expandAllNodes();
      }
      // For yearwise tree
      if (this.isYearwiseTreeExpanded) {
        this.expandAllYearwiseNodes();
      }
    }, 100);

    // Emit language change event to parent component
    this.languageChange.emit(this.selectedLanguage);
  }

  toggleAllNodes(): void {
    if (this.isTreeExpanded) {
      this.collapseAllNodes();
    } else {
      this.expandAllNodes();
    }
  }

  // Expand all nodes in the tree - ensure complete expansion
  expandAllNodes(): void {
    console.log('SingleTree - Expanding all nodes in topicwise tree');
    
    // Prevent concurrent expansion operations
    if (this.isExpandingNodes) {
      console.log('SingleTree - Already expanding nodes, ignoring request');
      return;
    }
    
    this.isExpandingNodes = true;
    this.isTreeExpanded = true;
    
    try {
      // Expand all nodes using the tree control
      this.treeData.forEach(node => {
        this.treeControl.expand(node);
        if (node.children && node.children.length > 0) {
          this.expandNestedNodes(node.children);
        }
      });
      
      console.log('SingleTree - All nodes expanded in topicwise tree');
    } finally {
      // Always clear the flag
      this.isExpandingNodes = false;
    }
  }
  
  private expandNestedNodes(nodes: SubjectNode[]): void {
    nodes.forEach(node => {
      this.treeControl.expand(node);
      if (node.children && node.children.length > 0) {
        this.expandNestedNodes(node.children);
      }
    });
  }

  // Expand all nodes in the yearwise tree
  expandAllYearwiseNodes(): void {
    console.log('SingleTree - Expanding all yearwise nodes');

    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      return;
    }

    // Set the internal expanded flag to true
    this.isYearwiseTreeExpanded = true;

    // Use the tree control's expandAll method for better performance
    if (this.yearwiseTreeControl.dataNodes) {
      this.yearwiseTreeControl.expandAll();
    } else {
      // If dataNodes is not set, set it first
      this.yearwiseTreeControl.dataNodes = this.yearwiseTreeData;
      this.yearwiseTreeControl.expandAll();
    }

    // Force refresh the tree view immediately without setTimeout delay
    this.yearwiseDataSource.data = [...this.yearwiseTreeData];
    this.changeDetectorRef.detectChanges();
  }

  // Collapse all nodes in the tree
  collapseAllNodes(): void {
    this.isTreeExpanded = false;
    if (this.treeData?.length) {
      this.treeControl.collapseAll();
    }
  }

  // Collapse all nodes in the yearwise tree
  collapseAllYearwiseNodes(): void {
    console.log('SingleTree - Collapsing all yearwise nodes');

    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      return;
    }

    // Set the internal expanded flag to false
    this.isYearwiseTreeExpanded = false;

    // Recursively collapse all nodes
    const collapseRecursively = (nodes: YearwiseNode[]) => {
      if (!nodes) return;

      nodes.forEach(node => {
        this.yearwiseTreeControl.collapse(node);

        if (node.children && node.children.length > 0) {
          collapseRecursively(node.children);
        }
      });
    };

    collapseRecursively(this.yearwiseTreeData);
  }

  // Check if node has children for the mat-tree
  hasChild = (_: number, node: SubjectNode) => !!node.children && node.children.length > 0;
  hasYearwiseChild = (_: number, node: YearwiseNode) => !!node.children && node.children.length > 0;

  // Handle node selection in topicwise mode
  onNodeSelect(node: SubjectNode): void {
    this.selectedNode = node;

    if (node.type === 'subject') {
      this.selectedSubject = node.name;
      this.selectedSubItem = null;
      this.selectedTopic = null;
      this.selectedSubTopic = null;
      this.subjectClick.emit(node.name);
    } else if (node.type === 'section') {
      this.selectedSubItem = node.name;
      this.selectedTopic = null;
      this.selectedSubTopic = null;
      this.subItemClick.emit(node.name);
    } else if (node.type === 'topic') {
      this.selectedTopic = node.name;
      this.selectedSubTopic = null;
      this.topicClick.emit(node.name);
    } else if (node.type === 'subtopic') {
      this.selectedSubTopic = node.name;
      this.subTopicClick.emit(node.name);
    }

    // Find the path from root to this node if not done already
    if (this.selectedNodePath.length === 0 || this.selectedNodePath[this.selectedNodePath.length - 1] !== node) {
      this.selectedNodePath = this.findNodePath(this.treeData, node);
    }

    // Emit the full hierarchy path to display in main-area's subheader
    const hierarchyPath = this.selectedNodePath.map(n => n.name);
    this.hierarchyPathChange.emit(hierarchyPath);

    // Force the view to update to ensure new styling is applied
    this.changeDetectorRef.detectChanges();
  }

  // Handle node selection in yearwise mode
  onYearwiseNodeSelect(node: YearwiseNode): void {

    // Clear previous selected node path to reset highlighting
    this.selectedYearwiseNodePath = [];
    this.selectedYearwiseNode = null;

    // Find the path from root to this node
    this.selectedYearwiseNodePath = this.findYearwiseNodePath(this.yearwiseTreeData, node);

    this.selectedYearwiseNode = node;

    // Expand all parent nodes in the path
    if (this.selectedYearwiseNodePath.length > 1) {
      // Skip the last node (which is the selected node itself)
      for (let i = 0; i < this.selectedYearwiseNodePath.length - 1; i++) {
        const parentNode = this.selectedYearwiseNodePath[i];
        this.yearwiseTreeControl.expand(parentNode);
      }
    }

    if (node.type === 'exam') {
      this.selectedYearwiseExam = node.name;
      this.selectedYearwiseLower = null;
      this.selectedYear = null;
      this.yearwiseExamClick.emit(node.name);

      // Automatically expand the exam node to show its children (papers)
      if (node.children && node.children.length > 0) {
        this.yearwiseTreeControl.expand(node);
      }
    } else if (node.type === 'examType') {
      this.selectedYearwiseLower = node.name;
      this.selectedYear = null;
      this.yearwiseLowerClick.emit(node.name);
    } else if (node.type === 'year') {
      this.selectedYear = node.name;
      this.yearClick.emit(node.name);
    }

    // Emit the full hierarchy path to display in main-area's subheader
    const hierarchyPath = this.selectedYearwiseNodePath.map(n => n.name);
    if (this.selectedYear && !hierarchyPath.includes(this.selectedYear)) {
      hierarchyPath.push(this.selectedYear);
    }
    this.hierarchyPathChange.emit(hierarchyPath);

    // Force refresh the tree to ensure correct styling is applied
    setTimeout(() => {
      this.yearwiseDataSource.data = [...this.yearwiseTreeData];
      this.changeDetectorRef.detectChanges();
    }, 0);
  }

  // Filter tree data to only include highlighted nodes and their ancestors
  filterTreeData<T extends SubjectNode | YearwiseNode>(nodes: T[], isRoot: boolean = false): T[] {
    if (!nodes || nodes.length === 0) return [];

    // Clone the array to avoid modifying the original
    let result: T[] = [];

    for (const node of nodes) {
      // Always keep the root nodes in filter mode, even if not highlighted
      const isHighlighted = node.isHighlighted || isRoot;

      // Process children first to determine if this node should be included
      let filteredChildren: T[] = [];
      if (node.children && node.children.length > 0) {
        // Recursive call to filter children (not root level)
        filteredChildren = this.filterTreeData(node.children as T[], false);
      }

      // Include this node if it's highlighted or has highlighted children
      if (isHighlighted || filteredChildren.length > 0) {
        // Clone the node to avoid modifying the original
        const clonedNode = { ...node };

        // Set children to filtered children
        if (filteredChildren.length > 0) {
          // Use type assertion to handle the generic type constraint
          (clonedNode.children as T[]) = filteredChildren;
        } else if (node.children && node.children.length > 0) {
          // Keep empty children array if original had children
          clonedNode.children = [];
        }

        result.push(clonedNode as T);
      }
    }

    return result;
  }

  // Helper method to expand all nodes in filtered trees
  expandAllFilteredNodes<T>(nodes: T[], treeControl: NestedTreeControl<T>): void {
    if (!nodes || nodes.length === 0) return;

    nodes.forEach(node => {
      // Expand each parent node
      treeControl.expand(node);

      // Also expand children with expandable children
      if ((node as any).children && (node as any).children.length > 0) {
        this.expandAllFilteredNodes((node as any).children, treeControl);
      }
    });
  }

  // Toggle search input visibility - IMPROVED
  toggleSearch(): void {
    this.isSearchExpanded = !this.isSearchExpanded;

    // Focus the input if expanded
    if (this.isSearchExpanded) {
      // Use a slightly longer timeout to ensure DOM has updated
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
          console.log('Setting focus on search input');
        } else {
          console.warn('Search input element not available');
        }
      }, 500); // Longer timeout to ensure DOM is ready
    } else {
      // Clear search if collapsed
      if (this.searchTerm) {
        this.clearSearch();
      }
    }
  }

  // Clear search term
  clearSearch(): void {
    this.searchTerm = '';
    this.onSearchInput();

    // Focus back on the search input after clearing
    if (this.isSearchExpanded && this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  // Handle search input changes
  onSearchInput(): void {
    // Debounce the search to prevent too many rapid updates
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      // Only perform search if term has 3 or more characters
      if (!this.searchTerm || this.searchTerm.trim().length < 3) {
        // If search is cleared or too short, reset the tree view
        this.dataSource.data = [...this.treeData];
        if (this.yearwiseTreeData) {
          this.yearwiseDataSource.data = [...this.yearwiseTreeData];
        }
        return;
      }

      console.log('Performing search with term:', this.searchTerm);

      // Reset all highlights first
      this.resetHighlights(this.treeData);
      if (this.yearwiseTreeData) {
        this.resetHighlights(this.yearwiseTreeData);
      }

      // Store search results
      this.searchResults = [];

      // Search in both trees
      if (this.p1Mode === 'topicwise') {
        this.findMatchingNodes(this.treeData, this.searchTerm.trim(), this.searchResults);
        this.dataSource.data = this.filterMode ?
          this.filterTreeData(this.treeData, true) :
          [...this.treeData];
      } else {
        this.findMatchingNodes(this.yearwiseTreeData, this.searchTerm.trim(), this.searchResults);
        this.yearwiseDataSource.data = this.filterMode ?
          this.filterTreeData(this.yearwiseTreeData, true) :
          [...this.yearwiseTreeData];
      }

      // Expand nodes to show matches
      if (this.searchResults.length > 0) {
        if (this.p1Mode === 'topicwise') {
          this.expandMatchedNodes(this.treeData, this.treeControl);
        } else {
          this.expandMatchedNodes(this.yearwiseTreeData, this.yearwiseTreeControl);
        }
      }

      // Emit search change event
      this.searchChange.emit({
        term: this.searchTerm.trim(),
        filterMode: this.filterMode
      });
    }, 500); // Increase timeout to 500ms
  }

  // Toggle filter mode
  toggleFilterMode(event: any): void {
    this.filterMode = event.checked;
    if (this.searchTerm) {
      this.performSearch();
    }
    // Emit search change event with updated filter mode
    this.searchChange.emit({
      term: this.searchTerm,
      filterMode: this.filterMode
    });
  }

  // Helper to count nodes in a tree for debugging
  private countNodes(nodes: SubjectNode[]): {total: number, byType: {[key: string]: number}} {
    if (!nodes) return {total: 0, byType: {}};

    const result = {
      total: 0,
      byType: {} as {[key: string]: number}
    };

    const countRecursively = (nodeArray: SubjectNode[]) => {
      if (!nodeArray) return;

      for (const node of nodeArray) {
        result.total++;
        result.byType[node.type] = (result.byType[node.type] || 0) + 1;

        if (node.children && node.children.length > 0) {
          countRecursively(node.children);
        }
      }
    };

    countRecursively(nodes);
    return result;
  }

  // Helper to ensure specific nodes are expanded
  private ensureNodeExpansion(nodeName: string): void {
    // Find the node in the tree data
    let nodeToExpand: SubjectNode | undefined;

    const findNode = (nodes: SubjectNode[]) => {
      for (const node of nodes) {
        if (node.name === nodeName) {
          nodeToExpand = node;
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findNode(node.children)) {
            return true;
          }
        }
      }
      return false;
    };

    findNode(this.treeData);

    if (nodeToExpand) {
      this.treeControl.expand(nodeToExpand);

      // Also expand any children
      if (nodeToExpand.children && nodeToExpand.children.length > 0) {
        nodeToExpand.children.forEach(child => {
          this.treeControl.expand(child);
        });
      }
    }
  }

  selectNode(node: any): void {
    // Find the path from root to this node
    this.selectedNodePath = this.findNodePath(this.treeData, node);

    // Set the selected node
    this.selectedNode = node;

    // Expand all parent nodes in the path
    if (this.selectedNodePath.length > 1) {
      // Skip the last node (which is the selected node itself)
      for (let i = 0; i < this.selectedNodePath.length - 1; i++) {
        const parentNode = this.selectedNodePath[i];
        this.treeControl.expand(parentNode);
      }
    }

    // Reuse existing selection logic
    if (node.type === 'topic') {
      this.selectedTopic = node.name;
      this.selectedSubTopic = null;
    } else if (node.type === 'subtopic') {
      this.selectedSubTopic = node.name;
    }

    this.onNodeSelect(node);

    // Emit the full hierarchy path to display in main-area's subheader
    const hierarchyPath = this.selectedNodePath.map(n => n.name);
    this.hierarchyPathChange.emit(hierarchyPath);

    // Force the view to update to ensure new styling is applied
    this.changeDetectorRef.detectChanges();
  }

  // Find the path from root to the target node
  findNodePath(nodes: SubjectNode[], targetNode: SubjectNode, currentPath: SubjectNode[] = []): SubjectNode[] {
    if (!nodes || nodes.length === 0) return [];

    for (const node of nodes) {
      // Create a new path including the current node
      const newPath = [...currentPath, node];

      // If this is the target node, return the path
      if (node === targetNode) {
        return newPath;
      }

      // If this node has children, search them
      if (node.children && node.children.length > 0) {
        const foundPath = this.findNodePath(node.children, targetNode, newPath);
        if (foundPath.length > 0) {
          return foundPath;
        }
      }
    }

    // Node not found in this branch
    return [];
  }

  toggleNode(event: Event, node: any): void {
    event.stopPropagation();
    if (this.treeControl.isExpanded(node)) {
      this.treeControl.collapse(node);
    } else {
      this.treeControl.expand(node);
    }
  }

  // Helper method to auto-select first items
  private autoSelectFirstItems(): void {
    console.log('SingleTree - Auto-selecting first items - START');

    if (!this.yearwiseTreeData || this.yearwiseTreeData.length === 0) {
      console.warn('SingleTree - No exams available to auto-select');
      return;
    }

    // Select the first exam node from the tree
    const firstExamNode = this.yearwiseTreeData[0];
    if (!firstExamNode) return;
    
    this.selectedYearwiseExam = firstExamNode.name;
    this.yearwiseExamClick.emit(firstExamNode.name);
    
    // Expand the exam node
    this.yearwiseTreeControl.expand(firstExamNode);

    // Find and select first paper if available
    if (firstExamNode.children && firstExamNode.children.length > 0) {
      const firstPaperNode = firstExamNode.children[0];
      if (!firstPaperNode) return;
      
      this.selectedYearwiseLower = firstPaperNode.name;
      this.yearwiseLowerClick.emit(firstPaperNode.name);
      
      // Expand the paper node
      this.yearwiseTreeControl.expand(firstPaperNode);

      // Find and select first year if available
      if (firstPaperNode.children && firstPaperNode.children.length > 0) {
        const firstYearNode = firstPaperNode.children[0];
        if (!firstYearNode) return;
        
        this.selectedYear = firstYearNode.name;
        this.yearClick.emit(firstYearNode.name);
        
        // Use the node selection method to handle all selection logic
        this.onYearwiseNodeSelect(firstYearNode);
      }
    }

    // Force UI update
    this.changeDetectorRef.detectChanges();
    console.log('SingleTree - Auto-selecting first items - COMPLETE');
  }

  // Toggle expand/collapse all yearwise nodes
  toggleAllYearwiseNodes(): void {
    if (this.isYearwiseTreeExpanded) {
      this.collapseAllYearwiseNodes();
      this.isYearwiseTreeExpanded = false;
    } else {
      this.expandAllYearwiseNodes();
      this.isYearwiseTreeExpanded = true;
    }
  }

  // Find the path from root to the target node for yearwise tree
  findYearwiseNodePath(nodes: YearwiseNode[], targetNode: YearwiseNode, currentPath: YearwiseNode[] = []): YearwiseNode[] {
    if (!nodes || nodes.length === 0) return [];

    for (const node of nodes) {
      // Create a new path including the current node
      const newPath = [...currentPath, node];

      // If this is the target node, return the path
      if (node === targetNode) {
        return newPath;
      }

      // If this node has children, search them
      if (node.children && node.children.length > 0) {
        const foundPath = this.findYearwiseNodePath(node.children, targetNode, newPath);
        if (foundPath.length > 0) {
          return foundPath;
        }
      }
    }

    // Node not found in this branch
    return [];
  }

  /**
   * Toggle edit mode
   */
  toggleEditMode(event: any): void {
    this.isEditMode = event.checked;
    
    // Emit edit mode change event to parent component
    this.editModeChange.emit(this.isEditMode);
    
    if (!this.isEditMode) {
      // Clear any active editing
      this.resetEditingState();
    }
  }

  /**
   * Reset any editing state
   */
  private resetEditingState(): void {
    this.editingNode = null;

    // Reset all node isEditing flags in the tree
    const resetRecursively = (nodes: SubjectNode[]) => {
      if (!nodes) return;
      
      for (const node of nodes) {
        if (node.isEditing) {
          node.isEditing = false;
        }
        
        if (node.children) {
          resetRecursively(node.children);
        }
      }
    };
    
    resetRecursively(this.treeData);
  }

  /**
   * Add a new node
   */
  addNode(parentNode: any): void {
    console.log('Adding node under:', parentNode);
    console.log('ParentNode properties:', Object.keys(parentNode));

    // Determine the correct parent_topic_id
    let parentTopicId = '';
    let itemLevel = 0;

    // First check if parent node has a level property we can use
    if (parentNode.level !== undefined) {
      // Use the appropriate level based on parent node type
      if (parentNode.type === 'subject') {
        itemLevel = 1; // For sections under subjects
      } else if (parentNode.type === 'section') {
        itemLevel = 2; // For topics under sections
      } else if (parentNode.type === 'topic') {
        itemLevel = 3; // For subtopics under topics
      } else if (parentNode.type === 'subtopic') {
        itemLevel = 4; // For items under subtopics
      }
    } else {
      // Fall back to determining level based on node type
      if (parentNode.type === 'subject') {
        itemLevel = 1; // For sections under subjects
      } else if (parentNode.type === 'section') {
        itemLevel = 2; // For topics under sections
      } else if (parentNode.type === 'topic') {
        itemLevel = 3; // For subtopics under topics
      } else if (parentNode.type === 'subtopic') {
        itemLevel = 4; // For items under subtopics
      }
    }

    // Determine parent_topic_id
    if (parentNode.topicId) {
      parentTopicId = parentNode.topicId;
    } else if (parentNode.children && parentNode.children.length > 0) {
      // Try to extract parent id pattern from children's topicIds if available
      for (const child of parentNode.children) {
        if (child.topicId) {
          // Extract parent part of topicId (e.g., "1.1" from "1.1.10")
          const parts = child.topicId.split('.');
          if (parts.length >= 2) {
            parentTopicId = parts.slice(0, 2).join('.');
            console.log(`Extracted parent_topic_id ${parentTopicId} from child's topicId ${child.topicId}`);
            break;
          }
        }
      }
    }

    // If still no ID, use default
    if (!parentTopicId) {
      parentTopicId = '2'; // Default for History if nothing else found
      console.log('Using default parent_topic_id:', parentTopicId);
    }

    console.log('Determined values - parent_topic_id:', parentTopicId, 'item_level:', itemLevel);

    // Open dialog to get new topic details
    const dialogRef = this.dialog.open(TopicDialogComponent, {
      width: '500px',
      data: {
        parentTopicId: parentTopicId,
        parentName: parentNode.name,
        parentLevel: parentNode.level || itemLevel - 1 // Pass parent's actual level for UI display
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Create API payload
        const payload = {
          name_eng: result.nameEng,
          name_hindi: result.nameHindi || undefined,
          parent_topic_id: parentTopicId,
          item_level: itemLevel
        };

        // Call API
        this.treeEditService.createTopic(payload).subscribe({
          next: (response) => {
            console.log('Topic created successfully:', response);

            // Add new node to tree
            if (parentNode.children) {
              const newNode = {
                name: result.nameEng,
                hindiName: result.nameHindi,
                type: this.getNodeTypeForLevel(itemLevel),
                level: itemLevel,
                topicId: response.topic.topic_id,
                hasChildren: false,
                children: []
              };

              parentNode.children.push(newNode);

              // Update tree data
              this.updateTreeData();

              // Expand the parent node to show the new child
              this.treeControl.expand(parentNode);

              // Notify user of success
              console.log('Node added successfully:', newNode);
            }
          },
          error: (error) => {
            console.error('Error creating topic:', error);
            // Show error dialog
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: `Failed to create topic: ${error.error?.message || 'Unknown error'}`,
                confirmText: 'OK',
                showCancel: false
              }
            });
          }
        });
      }
    });
  }

  /**
   * Start editing a node (inline)
   */
  editNode(node: any): void {
    try {
      console.log('EditNode method entered', new Date().toISOString());

      // Check if dialog service is properly injected
      if (!this.dialog) {
        console.error('Error: Dialog service is not available');
        return;
      }

      console.log('EditNode method called with node:', node);
      console.log('Node properties:', Object.keys(node));

      // Get topic ID - either from node directly or from topicIdMap
      let topicId = node.topicId;

      // If topicId is not available directly on the node, try to get it from the map
      if (!topicId && this.topicIdMap && this.topicIdMap[node.name]) {
        topicId = this.topicIdMap[node.name].id;
        console.log('Retrieved topicId from topicIdMap:', topicId);
      }

      console.log('Node topic ID (final):', topicId);

      if (!topicId) {
        console.error('Cannot rename: No topic ID found');
        // Show error message to user
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: `Cannot rename "${node.name}": Topic ID not found. The tree may need to be refreshed.`,
            confirmText: 'OK',
            cancelText: ''
          }
        });
        return;
      }

      console.log('Opening rename dialog');

      // Show rename dialog
      setTimeout(() => {
        try {
          const dialogRef = this.dialog.open(TopicDialogComponent, {
            width: '500px',
            data: {
              parentTopicId: topicId,
              parentName: node.name,
              parentLevel: node.level || 1, // Show actual level in UI
              isRename: true,
              existingNameEng: node.name,
              existingNameHindi: node.nameHindi,
              topicId: topicId
            }
          });

          console.log('Rename dialog opened successfully');

          dialogRef.afterClosed().subscribe(result => {
            console.log('Dialog closed with result:', result);
            if (result) {
              console.log('About to call rename API with topicId:', topicId);

              // Prepare API payload
              const payload = {
                name_eng: result.nameEng || undefined,
                name_hindi: result.nameHindi || undefined
              };

              // Ensure at least one name is provided
              if (!payload.name_eng && !payload.name_hindi) {
                console.error('Cannot save edit: No names provided');
                return;
              }

              // Call rename API
              this.treeEditService.renameTopic(topicId, payload).subscribe({
                next: (response) => {
                  console.log('Topic renamed successfully:', response);

                  // Update node data
                  node.name = response.topic.name_eng;
                  if (response.topic.name_hindi) {
                    node.nameHindi = response.topic.name_hindi;
                    // Also update hindi name map
                    if (this.hindiNameMap) {
                      this.hindiNameMap[node.name] = response.topic.name_hindi;
                    }
                  }

                  // Update UI
                  this.updateTreeData();
                },
                error: (error) => {
                  console.error('Error renaming topic:', error);

                  let errorMessage = 'Unknown error occurred';
                  if (error.status === 400) {
                    errorMessage = 'Cannot rename topic: Bad request.';
                  } else if (error.status === 404) {
                    errorMessage = 'Topic not found.';
                  } else if (error.error?.message) {
                    errorMessage = error.error.message;
                  }

                  // Show error message
                  this.dialog.open(ConfirmDialogComponent, {
                    width: '400px',
                    data: {
                      title: 'Error',
                      message: `Failed to rename topic: ${errorMessage}`,
                      confirmText: 'OK',
                      cancelText: ''
                    }
                  });
                }
              });
            }
          });
        } catch (dialogError) {
          console.error('Error opening dialog:', dialogError);
        }
      }, 100); // Small delay before opening dialog
    } catch (error) {
      console.error('Error in editNode method:', error);
    }
  }

  /**
   * Delete a node
   */
  deleteNode(node: any): void {
    console.log('Deleting node:', node);

    // Get topic ID
    const topicId = node.topicId;
    if (!topicId) {
      console.error('Cannot delete: No topic ID found');
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete "${node.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Call delete API
        this.treeEditService.deleteTopic(topicId).subscribe({
          next: (response) => {
            console.log('Topic deleted successfully:', response);

            // Find parent node to remove this node from its children
            this.removeNodeFromTree(node);

            // Update UI
            this.updateTreeData();
          },
          error: (error) => {
            console.error('Error deleting topic:', error);

            let errorMessage = 'Unknown error occurred';
            if (error.status === 400) {
              errorMessage = 'Cannot delete: This topic has children. Delete all children first.';
            } else if (error.status === 404) {
              errorMessage = 'Topic not found.';
            } else if (error.error?.message) {
              errorMessage = error.error.message;
            }

            // Show error message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: `Failed to delete topic: ${errorMessage}`,
                confirmText: 'OK',
                cancelText: ''
              }
            });
          }
        });
      }
    });
  }

  /**
   * Remove a node from the tree
   */
  private removeNodeFromTree(nodeToRemove: SubjectNode): void {
    // Helper function to recursively search and remove node
    const findAndRemove = (nodes: SubjectNode[]): boolean => {
      if (!nodes) return false;

      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i] === nodeToRemove) {
          // Found the node, remove it
          nodes.splice(i, 1);
          return true;
        }

        // Check children - make sure to use safe navigation
        const children = nodes[i].children || [];
        if (children.length > 0) {
          if (findAndRemove(children)) {
            return true;
          }
        }
      }

      return false;
    };

    // Start the search from the root - ensure treeData is not undefined
    if (this.treeData && this.treeData.length > 0) {
      findAndRemove(this.treeData);
    }
  }

  /**
   * Check if node can be added (up to level 6)
   */
  canAddNode(node: any): boolean {
    return (node.level || 0) < 6;
  }

  /**
   * Check if node can be edited/deleted (up to level 7)
   */
  canEditDeleteNode(node: any): boolean {
    return (node.level || 0) <= 7;
  }

  // Helper method to check user role
  private checkUserRole(): void {
    this.isInternalUser = this.roleService.getRole() === 'internal';
    console.log('SingleTree - User role check: isInternalUser =', this.isInternalUser);
  }

  /**
   * Get the node type based on level
   */
  getNodeTypeForLevel(level: number): 'subject' | 'section' | 'topic' | 'subtopic' {
    switch(level) {
      case 0:
        return 'subject';
      case 1:
        return 'section';  // For topics under subjects (level 1)
      case 2:
        return 'topic';    // For subtopics under sections (level 2)
      case 3:
        return 'subtopic'; // For subtopics under topics (level 3)
      default:
        return 'subtopic'; // For deeper levels
    }
  }

  refreshTopicTree(): void {
    console.log('SingleTree - Refreshing topic tree');

    // Exit early if refresh is already in progress
    if (this.isRefreshInProgress) {
      console.log('SingleTree - Refresh already in progress, ignoring request');
      return;
    }

    // Set flag to prevent concurrent refreshes
    this.isRefreshInProgress = true;

    // If in 1-column view, directly get data from API rather than using pre-loaded data
    if (this.currentLayoutView === '1-column') {
      console.log('SingleTree - In 1-column view, loading data directly from API');
      
      // Use the tree service to get data directly from API
      this.treeService.refreshTree()
        .then(() => {
          console.log('SingleTree - Tree data refreshed from API successfully');
          this.isRefreshInProgress = false;
          
          // The treeService subscription in ngOnInit will update treeData automatically
        })
        .catch(error => {
          console.error('SingleTree - Error refreshing tree data:', error);
          this.isRefreshInProgress = false;
        });
    } else {
      console.log('SingleTree - Using existing data, no need for API call');
      // Just update the local UI without making API calls
      this.updateTreeData();
      this.isRefreshInProgress = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper method to build the topicwise tree from the input data
  private buildTopicwiseTree(subjectName: string): SubjectNode[] {
    // If subject has sections
    if (this.p1SubMap[subjectName]) {
      return this.p1SubMap[subjectName].map((sectionName: string) => {
        const sectionInfo = this.topicIdMap && this.topicIdMap[sectionName];
        const section: SubjectNode = {
          name: sectionName,
          type: 'section',
          nameHindi: (sectionInfo && sectionInfo.hindiName) || this.hindiNameMap[sectionName] || '',
          children: []
        };

        // If section has topics
        if (this.p2TopicsMap[sectionName]) {
          section.children = this.p2TopicsMap[sectionName].map((topicName: string) => {
            const topicInfo = this.topicIdMap && this.topicIdMap[topicName];
            const topic: SubjectNode = {
              name: topicName,
              type: 'topic',
              nameHindi: (topicInfo && topicInfo.hindiName) || this.hindiNameMap[topicName] || '',
              children: []
            };

            // If topic has subtopics
            if (this.p2SubTopicsMap[topicName]) {
              topic.children = this.p2SubTopicsMap[topicName].map((subtopicName: string) => {
                const subtopicInfo = this.topicIdMap && this.topicIdMap[subtopicName];
                return {
                  name: subtopicName,
                  type: 'subtopic',
                  nameHindi: (subtopicInfo && subtopicInfo.hindiName) || this.hindiNameMap[subtopicName] || '',
                  children: [] // Leaf node
                };
              });
            }

            return topic;
          });
        }

        return section;
      });
    }

    return [];
  }

  // Helper method to build tree from API data
  private buildTopicwiseTreeFromData(treeData: any, parentNode: SubjectNode): void {
    // Process sections
    if (treeData.sections) {
      parentNode.children = Object.keys(treeData.sections).map(sectionName => {
        const section = treeData.sections[sectionName];
        const sectionNode: SubjectNode = {
          name: sectionName,
          type: 'section',
          topicId: section.id,
          nameHindi: section.hindiName || this.hindiNameMap[sectionName] || '',
          children: []
        };
        
        // Process topics
        if (section.topics) {
          sectionNode.children = Object.keys(section.topics).map(topicName => {
            const topic = section.topics[topicName];
            const topicNode: SubjectNode = {
              name: topicName,
              type: 'topic',
              topicId: topic.id,
              nameHindi: topic.hindiName || this.hindiNameMap[topicName] || '',
              children: []
            };
            
            // Process subtopics
            if (topic.subtopics) {
              topicNode.children = Object.keys(topic.subtopics).map(subtopicName => {
                const subtopic = topic.subtopics[subtopicName];
                return {
                  name: subtopicName,
                  type: 'subtopic',
                  topicId: subtopic.id,
                  nameHindi: subtopic.hindiName || this.hindiNameMap[subtopicName] || '',
                  children: []
                };
              });
            }
            
            return topicNode;
          });
        }
        
        return sectionNode;
      });
    }
  }

  /**
   * Add a new subject at the root level (parent_topic_id = 0, item_level = 1)
   */
  addSubject(): void {
    console.log('Adding a new subject (root level)');
    
    // Open dialog to get new subject details
    const dialogRef = this.dialog.open(TopicDialogComponent, {
      width: '500px',
      data: {
        parentTopicId: '0', // Root level
        parentName: 'Root',
        parentLevel: 0,      // Root level (subjects are level 1)
        isSubject: true      // Flag to indicate this is a top-level subject
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Create API payload for new subject
        const payload = {
          name_eng: result.nameEng,
          name_hindi: result.nameHindi || undefined,
          parent_topic_id: '0',    // Root level parent
          item_level: 1            // Level 1 (subject)
        };

        // Call API to create the subject
        this.treeEditService.createTopic(payload).subscribe({
          next: (response) => {
            console.log('Subject created successfully:', response);
            
            // Refresh the tree to show the new subject with complete data
            this.refreshTopicTree();
            
            // Show success message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Success',
                message: `Subject "${result.nameEng}" created successfully`,
                confirmText: 'OK',
                showCancel: false
              }
            });
          },
          error: (error) => {
            console.error('Error creating subject:', error);
            
            // Show error message
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: `Failed to create subject: ${error.error?.message || 'Unknown error'}`,
                confirmText: 'OK',
                showCancel: false
              }
            });
          }
        });
      }
    });
  }
}

/**   * Refresh the topic tree by clearing the cache and reloading data   */

